import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import clsx from 'clsx';
import { useWizardStore } from '../stores/wizardStore';
import { WIZARD_STEPS } from '../types/wizard';
import { fetchTemplate, fetchPlaybook, startGeneration, createTemplate } from '../lib/api';
import ProjectContextStep from '../components/wizard/ProjectContextStep';
import TechStackStep from '../components/wizard/TechStackStep';
import TeamStructureStep from '../components/wizard/TeamStructureStep';
import GitWorkflowStep from '../components/wizard/GitWorkflowStep';
import CodingStandardsStep from '../components/wizard/CodingStandardsStep';
import MCPConfigStep from '../components/wizard/MCPConfigStep';
import CICDStep from '../components/wizard/CICDStep';
import PluginsStep from '../components/wizard/PluginsStep';
import ReviewStep from '../components/wizard/ReviewStep';

const stepComponents = [
  ProjectContextStep,
  TechStackStep,
  TeamStructureStep,
  GitWorkflowStep,
  CodingStandardsStep,
  MCPConfigStep,
  CICDStep,
  PluginsStep,
  ReviewStep,
];

export default function WizardPage() {
  const { step } = useParams();
  const navigate = useNavigate();
  const {
    currentStep,
    setStep,
    nextStep,
    prevStep,
    projectContext,
    techStack,
    teamStructure,
    gitWorkflow,
    codingStandards,
    mcpConfig,
    cicd,
    plugins,
    selectedPlaybook,
    setSelectedPlaybook,
  } = useWizardStore();

  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  const playbookParam = searchParams.get('playbook');
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [playbookLoaded, setPlaybookLoaded] = useState(false);

  // Pre-populate wizard from template
  useEffect(() => {
    if (!templateId || templateLoaded) return;
    const loadTemplate = async () => {
      try {
        const tmpl = await fetchTemplate(templateId);
        if (tmpl.wizardData) {
          const w = tmpl.wizardData;
          if (w.projectContext) useWizardStore.getState().updateProjectContext(w.projectContext);
          if (w.techStack) useWizardStore.getState().updateTechStack(w.techStack);
          if (w.teamStructure) useWizardStore.getState().updateTeamStructure(w.teamStructure);
          if (w.gitWorkflow) useWizardStore.getState().updateGitWorkflow(w.gitWorkflow);
          if (w.codingStandards) useWizardStore.getState().updateCodingStandards(w.codingStandards);
          if (w.mcpConfig) useWizardStore.getState().updateMCPConfig(w.mcpConfig);
          if (w.cicd) useWizardStore.getState().updateCICD(w.cicd);
          if (w.plugins) useWizardStore.getState().updatePlugins(w.plugins);
        }
      } catch {
        // Failed to load template
      }
      setTemplateLoaded(true);
    };
    loadTemplate();
  }, [templateId, templateLoaded]);

  // Pre-populate wizard from playbook URL param
  useEffect(() => {
    if (!playbookParam || playbookLoaded) return;
    const loadPlaybook = async () => {
      try {
        const pb = await fetchPlaybook(playbookParam);
        if (pb) {
          setSelectedPlaybook(pb.id);
          // Map playbook projectType to a goal label the wizard recognises
          const goalLabel = pb.projectType
            .replace(/-/g, ' ')
            .replace(/\b\w/g, (c: string) => c.toUpperCase());
          useWizardStore.getState().updateProjectContext({ goal: goalLabel });
        }
      } catch {
        // Playbook not found — continue without it
      }
      setPlaybookLoaded(true);
    };
    loadPlaybook();
  }, [playbookParam, playbookLoaded, setSelectedPlaybook]);

  useEffect(() => {
    if (step !== undefined) {
      const stepNum = parseInt(step, 10);
      if (!isNaN(stepNum) && stepNum >= 0 && stepNum <= 8) {
        setStep(stepNum);
      }
    }
  }, [step, setStep]);

  const handleStepClick = (index: number) => {
    setStep(index);
    navigate(`/wizard/${index}`);
  };

  const handleNext = () => {
    if (currentStep < 8) {
      nextStep();
      navigate(`/wizard/${currentStep + 1}`);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      prevStep();
      navigate(`/wizard/${currentStep - 1}`);
    }
  };

  const handleGenerate = async () => {
    try {
      // Ensure goal is set — fall back to selectedPlaybook ID if user came via ?playbook=
      const resolvedContext = {
        ...projectContext,
        goal: projectContext.goal || selectedPlaybook || '',
      };
      const generation = await startGeneration({
        name: projectContext.name || 'Untitled Project',
        wizardData: {
          projectContext: resolvedContext,
          techStack,
          teamStructure,
          gitWorkflow,
          codingStandards,
          mcpConfig,
          cicd,
          plugins,
        },
      });
      navigate(`/generations/${generation.id}`);
    } catch {
      // For demo purposes navigate to generations list
      navigate('/generations');
    }
  };

  const StepComponent = stepComponents[currentStep];
  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  return (
    <div className="min-h-screen pt-28 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white mb-2">
            Blueprint Configuration Wizard
          </h1>
          <p className="text-gray-400">
            Step {currentStep + 1} of {WIZARD_STEPS.length}:{' '}
            <span className="text-white">{WIZARD_STEPS[currentStep]}</span>
          </p>
        </div>

        {/* Stepper */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            {WIZARD_STEPS.map((label, index) => (
              <button
                key={label}
                onClick={() => handleStepClick(index)}
                className={clsx(
                  'flex flex-col items-center gap-2 group relative',
                  'transition-all duration-200'
                )}
              >
                <div
                  className={clsx(
                    'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 border-2',
                    index < currentStep &&
                      'bg-success border-success text-white',
                    index === currentStep &&
                      'bg-accent border-accent text-white shadow-lg shadow-accent/30',
                    index > currentStep &&
                      'bg-surface-DEFAULT border-dark-border text-gray-500 group-hover:border-surface-300'
                  )}
                >
                  {index < currentStep ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={clsx(
                    'text-[10px] font-medium hidden lg:block max-w-[80px] text-center leading-tight',
                    index === currentStep ? 'text-white' : 'text-gray-500'
                  )}
                >
                  {label}
                </span>
              </button>
            ))}
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-surface-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent to-blue-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="card mb-8">
          <StepComponent />
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={clsx(
              'btn-secondary',
              currentStep === 0 && 'opacity-50 cursor-not-allowed'
            )}
          >
            Previous
          </button>
          <div className="flex gap-3">
            {currentStep === 8 ? (
              <button onClick={handleGenerate} className="btn-primary">
                Generate Blueprint
              </button>
            ) : (
              <button onClick={handleNext} className="btn-primary">
                Next Step
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
